import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { QuizState, Question } from '../../types';
import {
  careRecipientQuestions,
  legalRepresentativeQuestions,
  preventedCaregiverQuestions,
  substituteCaregiverQuestions,
  commercialProviderQuestions,
  privateIndividualQuestions,
} from '../../config/quizQuestions';

// Deep clone helper for questions arrays
const cloneQuestions = (questions: Question[]): Question[] =>
  questions.map((q) => ({
    ...q,
    answers: typeof q.answers === 'object' ? { ...q.answers } : q.answers,
  }));

const initialState: QuizState = {
  careRecipient: {
    questions: cloneQuestions(careRecipientQuestions),
    currentIndex: 0,
  },
  legalRepresentative: {
    questions: cloneQuestions(legalRepresentativeQuestions),
    currentIndex: 0,
  },
  preventedCaregiver: {
    questions: cloneQuestions(preventedCaregiverQuestions),
    currentIndex: 0,
  },
  substituteCaregiver: {
    questions: cloneQuestions(substituteCaregiverQuestions),
    currentIndex: 0,
  },
  commercialProvider: {
    questions: cloneQuestions(commercialProviderQuestions),
    currentIndex: 0,
  },
  privateIndividual: {
    questions: cloneQuestions(privateIndividualQuestions),
    currentIndex: 0,
  },
  currentStep: 1,
  editSummary: '',
  chosenPlan: '',
  availableDays: '',
  isEditing: false,
  isCopying: false,
  editedPreviousForm: false,
  startFromJuly: null,
};

type SectionKey =
  | 'careRecipient'
  | 'legalRepresentative'
  | 'preventedCaregiver'
  | 'substituteCaregiver'
  | 'commercialProvider'
  | 'privateIndividual';

const quizSlice = createSlice({
  name: 'quiz',
  initialState,
  reducers: {
    // Set answer for a specific question
    // Answer can be string or object (for group type with date/duration/amount)
    setQuestionAnswer: (
      state,
      action: PayloadAction<{
        section: SectionKey;
        questionKey: string;
        answer: string | { date: string; duration: string; amount: string };
        fileName?: string;
      }>
    ) => {
      const { section, questionKey, answer, fileName } = action.payload;
      const question = state[section].questions.find((q) => q.questionKey === questionKey);
      if (question) {
        question.answers = answer;
        if (fileName) {
          question.fileName = fileName;
        }
      }
    },

    // Set answer by index (supports both string and object answers)
    setAnswerByIndex: (
      state,
      action: PayloadAction<{
        section: SectionKey;
        index: number;
        answer: string | { date: string; duration: string; amount: string };
        fileName?: string;
      }>
    ) => {
      const { section, index, answer, fileName } = action.payload;
      const sectionState = state[section];
      
      if (sectionState && sectionState.questions && index >= 0 && index < sectionState.questions.length) {
        const question = sectionState.questions[index];
        if (question) {
          // For object answers, spread to create a new object reference
          if (typeof answer === 'object' && answer !== null) {
            question.answers = { ...answer };
          } else {
            question.answers = answer;
          }
          if (fileName) {
            question.fileName = fileName;
          }
        }
      }
    },

    // Set return question (warning message) and optional link for a specific question
    setReturnQuestion: (
      state,
      action: PayloadAction<{
        section: SectionKey;
        questionKey: string;
        message: string;
        link?: string;
      }>
    ) => {
      const { section, questionKey, message, link } = action.payload;
      const question = state[section].questions.find((q) => q.questionKey === questionKey);
      if (question) {
        question.returnQuestion = message;
        question.returnLink = link || '';
      }
    },

    // Update current index for a section
    setCurrentIndex: (
      state,
      action: PayloadAction<{
        section: SectionKey;
        index: number;
      }>
    ) => {
      const { section, index } = action.payload;
      state[section].currentIndex = index;
    },

    // Increment current index
    incrementIndex: (state, action: PayloadAction<SectionKey>) => {
      const section = action.payload;
      if (state[section].currentIndex < state[section].questions.length - 1) {
        state[section].currentIndex += 1;
      }
    },

    // Decrement current index
    decrementIndex: (state, action: PayloadAction<SectionKey>) => {
      const section = action.payload;
      if (state[section].currentIndex > 0) {
        state[section].currentIndex -= 1;
      }
    },

    // Set current step (which section we're on)
    setCurrentStep: (state, action: PayloadAction<number>) => {
      state.currentStep = action.payload;
    },

    // Set chosen plan
    setChosenPlan: (state, action: PayloadAction<'free' | 'smart' | ''>) => {
      state.chosenPlan = action.payload;
    },

    // Set edit summary (which field is being edited)
    setEditSummary: (state, action: PayloadAction<string>) => {
      state.editSummary = action.payload;
    },

    // Set available days
    setAvailableDays: (state, action: PayloadAction<string>) => {
      state.availableDays = action.payload;
    },

    // Insert additional date questions for Commercial Provider section
    insertCommercialProviderDateQuestion: (state) => {
      const questions = state.commercialProvider.questions;
      const currentIndex = state.commercialProvider.currentIndex;
      
      // Find the position after current durationOfAbsence question
      // Insert new availableDates + durationOfAbsence question pair
      const newQuestions = [
        {
          question: '',
          answers: { date: '', duration: '', amount: '' },
          type: 'group' as const,
          required: false,
          questionKey: 'availableDates',
        },
        {
          question: 'War die abwesende Pflegeperson den ganzen Tag verhindert?',
          answers: '',
          type: 'submit' as const,
          required: false,
          questionKey: 'durationOfAbsence',
        },
      ];
      
      // Insert after current index
      questions.splice(currentIndex + 1, 0, ...newQuestions);
    },

    // Insert additional date questions for Private Individual section
    insertPrivateIndividualDateQuestion: (state) => {
      const questions = state.privateIndividual.questions;
      const currentIndex = state.privateIndividual.currentIndex;
      
      // Insert new availableDates + durationOfAbsence question pair
      const newQuestions = [
        {
          question: '',
          answers: { date: '', duration: '', amount: '' },
          type: 'group' as const,
          required: false,
          questionKey: 'availableDates',
        },
        {
          question: 'War die abwesende Pflegeperson den ganzen Tag verhindert?',
          answers: '',
          type: 'submit' as const,
          required: false,
          questionKey: 'durationOfAbsence',
        },
      ];
      
      // Insert after current index
      questions.splice(currentIndex + 1, 0, ...newQuestions);
    },

    // Set editing mode
    setIsEditing: (state, action: PayloadAction<boolean>) => {
      state.isEditing = action.payload;
    },

    // Set copying mode
    setIsCopying: (state, action: PayloadAction<boolean>) => {
      state.isCopying = action.payload;
    },

    // Set edited previous form flag
    setEditedPreviousForm: (state, action: PayloadAction<boolean>) => {
      state.editedPreviousForm = action.payload;
    },

    // Set start from July flag (true = after July 2025, false = before July 2025)
    // When true, filter out the carePeriod question from preventedCaregiver
    setStartFromJuly: (state, action: PayloadAction<boolean>) => {
      state.startFromJuly = action.payload;
      
      // When starting from July, remove the carePeriod question (legacy app behavior)
      // This question is not applicable for forms starting from July 2025
      if (action.payload === true && !state.isEditing && !state.isCopying) {
        state.preventedCaregiver.questions = state.preventedCaregiver.questions.filter(
          (q) => q.questionKey !== 'carePeriod'
        );
      }
    },

    // Load quiz data from API response
    loadQuizData: (
      state,
      action: PayloadAction<{
        section: SectionKey;
        questions: Question[];
        currentIndex: number;
      }>
    ) => {
      const { section, questions, currentIndex } = action.payload;
      state[section].questions = questions;
      state[section].currentIndex = currentIndex;
    },

    // Reset a specific section
    resetSection: (state, action: PayloadAction<SectionKey>) => {
      const section = action.payload;
      switch (section) {
        case 'careRecipient':
          state.careRecipient = {
            questions: cloneQuestions(careRecipientQuestions),
            currentIndex: 0,
          };
          break;
        case 'legalRepresentative':
          state.legalRepresentative = {
            questions: cloneQuestions(legalRepresentativeQuestions),
            currentIndex: 0,
          };
          break;
        case 'preventedCaregiver':
          state.preventedCaregiver = {
            questions: cloneQuestions(preventedCaregiverQuestions),
            currentIndex: 0,
          };
          break;
        case 'substituteCaregiver':
          state.substituteCaregiver = {
            questions: cloneQuestions(substituteCaregiverQuestions),
            currentIndex: 0,
          };
          break;
        case 'commercialProvider':
          state.commercialProvider = {
            questions: cloneQuestions(commercialProviderQuestions),
            currentIndex: 0,
          };
          break;
        case 'privateIndividual':
          state.privateIndividual = {
            questions: cloneQuestions(privateIndividualQuestions),
            currentIndex: 0,
          };
          break;
      }
    },

    // Reset all quiz state
    resetQuiz: () => ({
      ...initialState,
      careRecipient: {
        questions: cloneQuestions(careRecipientQuestions),
        currentIndex: 0,
      },
      legalRepresentative: {
        questions: cloneQuestions(legalRepresentativeQuestions),
        currentIndex: 0,
      },
      preventedCaregiver: {
        questions: cloneQuestions(preventedCaregiverQuestions),
        currentIndex: 0,
      },
      substituteCaregiver: {
        questions: cloneQuestions(substituteCaregiverQuestions),
        currentIndex: 0,
      },
      commercialProvider: {
        questions: cloneQuestions(commercialProviderQuestions),
        currentIndex: 0,
      },
      privateIndividual: {
        questions: cloneQuestions(privateIndividualQuestions),
        currentIndex: 0,
      },
    }),
  },
});

export const {
  setQuestionAnswer,
  setAnswerByIndex,
  setReturnQuestion,
  setCurrentIndex,
  incrementIndex,
  decrementIndex,
  setCurrentStep,
  setChosenPlan,
  setEditSummary,
  setAvailableDays,
  insertCommercialProviderDateQuestion,
  insertPrivateIndividualDateQuestion,
  setIsEditing,
  setIsCopying,
  setEditedPreviousForm,
  setStartFromJuly,
  loadQuizData,
  resetSection,
  resetQuiz,
} = quizSlice.actions;

export default quizSlice.reducer;

